import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import glassTheme from '../styles/glassTheme';
import { consentApi, ConsentNotice, ConsentScopeState } from '../services/consentApi';

/**
 * Research consent prompt.
 *
 * The platform's student data is human-subjects research data. Without a way
 * for a participant to actually record a decision, consent coverage can never
 * move off zero and no research use is defensible — so this component is the
 * functional half of the consent system, not decoration.
 *
 * Design constraints, deliberate:
 *   - Declining is exactly as easy as agreeing. No dark patterns, no pre-ticked
 *     box, no "remind me later" that behaves like consent.
 *   - The prompt is dismissible without deciding, and dismissal is NOT consent.
 *   - The full notice is shown before the choice, not behind a link.
 *   - The participant can change the decision later from the same component
 *     rendered in settings mode.
 */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Panel = styled.div`
  background: ${glassTheme.colors.background};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 12px;
  max-width: 560px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 1.75rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Title = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
  color: ${glassTheme.colors.text.primary};
`;

const Summary = styled.p`
  margin: 0 0 1rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.6;
`;

const PointList = styled.ul`
  margin: 0 0 1.5rem;
  padding-left: 1.2rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.7;

  li {
    margin-bottom: 0.4rem;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

/* Both choices share the same weight; neither is visually privileged. */
const Choice = styled.button<{ $variant: 'agree' | 'decline' }>`
  flex: 1;
  min-width: 140px;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  border: 1px solid ${props => props.$variant === 'agree'
    ? `${glassTheme.colors.status.success}88`
    : `${glassTheme.colors.border}`};
  background: ${props => props.$variant === 'agree'
    ? `${glassTheme.colors.status.success}22`
    : 'transparent'};
  color: ${glassTheme.colors.text.primary};

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Later = styled.button`
  background: none;
  border: none;
  color: ${glassTheme.colors.text.muted};
  cursor: pointer;
  font-size: 0.85rem;
  margin-top: 1rem;
  text-decoration: underline;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  margin-bottom: 0.75rem;
`;

const DISMISS_KEY = 'consent_prompt_dismissed_v1';

interface Props {
  /** 'prompt' blocks with a modal; 'settings' renders an inline manager. */
  mode?: 'prompt' | 'settings';
}

export const ConsentPrompt: React.FC<Props> = ({ mode = 'prompt' }) => {
  const [notice, setNotice] = useState<ConsentNotice | null>(null);
  const [scopes, setScopes] = useState<ConsentScopeState[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );

  const load = async () => {
    try {
      setLoading(true);
      const [n, mine] = await Promise.all([consentApi.getNotice(), consentApi.getMine()]);
      setNotice(n);
      setScopes(mine.scopes);
    } catch (err) {
      console.error('Failed to load consent state:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (status: 'granted' | 'declined' | 'withdrawn') => {
    try {
      setSaving(true);
      await consentApi.record('research', status);
      await load();
    } catch (err) {
      console.error('Failed to record consent:', err);
    } finally {
      setSaving(false);
    }
  };

  const research = scopes.find(s => s.scope === 'research');
  const undecided = !research || research.status === 'not_asked' || research.needsReconfirm;

  const statusLabel = (s: string) =>
    s === 'granted' ? '已同意'
      : s === 'declined' ? '已拒绝'
        : s === 'withdrawn' ? '已撤回'
          : '尚未选择';

  if (loading || !notice) return null;

  // ---- settings mode: always visible, lets the participant change their mind
  if (mode === 'settings') {
    return (
      <div>
        <Title>{notice.title}</Title>
        <Summary>{notice.summary}</Summary>
        <PointList>
          {notice.points.map((p, i) => <li key={i}>{p}</li>)}
        </PointList>
        <StatusRow>
          <div>
            <div style={{ color: glassTheme.colors.text.primary }}>研究数据使用</div>
            <div style={{ fontSize: '0.8rem', color: glassTheme.colors.text.muted }}>
              当前状态：{statusLabel(research ? research.status : 'not_asked')}
            </div>
          </div>
          <Actions style={{ flex: '0 0 auto' }}>
            {research?.status === 'granted' ? (
              <Choice $variant="decline" disabled={saving} onClick={() => decide('withdrawn')}>
                撤回同意
              </Choice>
            ) : (
              <Choice $variant="agree" disabled={saving} onClick={() => decide('granted')}>
                同意
              </Choice>
            )}
          </Actions>
        </StatusRow>
      </div>
    );
  }

  // ---- prompt mode: only when a decision is genuinely outstanding
  if (!undecided || dismissed) return null;

  return (
    <Backdrop>
      <Panel>
        <Title>{notice.title}</Title>
        <Summary>{notice.summary}</Summary>
        <PointList>
          {notice.points.map((p, i) => <li key={i}>{p}</li>)}
        </PointList>
        <Actions>
          <Choice $variant="agree" disabled={saving} onClick={() => decide('granted')}>
            我同意
          </Choice>
          <Choice $variant="decline" disabled={saving} onClick={() => decide('declined')}>
            我不同意
          </Choice>
        </Actions>
        <div style={{ textAlign: 'center' }}>
          {/* Dismissal is explicitly not consent; the prompt returns next session. */}
          <Later onClick={() => { sessionStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}>
            稍后再决定
          </Later>
        </div>
      </Panel>
    </Backdrop>
  );
};

export default ConsentPrompt;
